import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Switch, Tag, Space, Select, Radio, Card,
  message, Empty, Tooltip, Popconfirm, Alert, Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StarFilled,
  LinkOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';

const font = { fontFamily: 'CustomFont' };

/**
 * درگاه‌های پرداخت آنلاین شرکت. فیلدهای اطلاعات هر درگاه از API (drivers[].fields)
 * خوانده می‌شود، پس برای درگاه جدید نیازی به تغییر این فرم نیست.
 */
const OnlineGatewaysTab = forwardRef((_, ref) => {
  const [form] = Form.useForm();
  const [linkForm] = Form.useForm();
  const [gateways, setGateways] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [domain, setDomain] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainError, setDomainError] = useState(null);
  const [editing, setEditing] = useState(null);
  const selectedDriverKey = Form.useWatch('driver', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeSettingsApi.getOnlineGateways();
      const data = res?.data || res || {};
      setGateways(data.gateways || []);
      setDrivers(data.drivers || []);
      setDomain(data.domain || null);
      setDomainInput(data.domain?.payment_domain || '');
      const days = data.settings?.public_link_expiry_days;
      linkForm.setFieldsValue({
        mode: days === null || days === undefined ? 'never' : 'days',
        days: days ?? 7,
      });
    } catch (error) {
      console.error('Error loading online gateways:', error);
      message.error('خطا در دریافت درگاه‌های آنلاین');
    } finally {
      setLoading(false);
    }
  }, [linkForm]);

  useEffect(() => {
    load();
  }, [load]);

  const driverOf = (key) => drivers.find((d) => d.driver === key);
  const selectedDriver = driverOf(editing ? editing.driver : selectedDriverKey);

  const handleAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      driver: drivers[0]?.driver,
      is_active: true,
      is_sandbox: false,
      is_default: gateways.length === 0,
    });
    setModalVisible(true);
  };

  useImperativeHandle(ref, () => ({ openAddModal: handleAdd }));

  const handleEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      driver: record.driver,
      title: record.title,
      is_active: record.is_active,
      is_sandbox: record.is_sandbox,
      is_default: record.is_default,
      credentials: Object.fromEntries(
        (driverOf(record.driver)?.fields || [])
          // مقدار فیلدهای محرمانه هیچ‌وقت از سرور نمی‌آید؛ خالی بماند یعنی تغییر نکند
          .map((f) => [f.name, f.secret ? '' : record.credentials?.[f.name] ?? '']),
      ),
    });
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        title: values.title,
        is_active: !!values.is_active,
        is_sandbox: !!values.is_sandbox,
        is_default: !!values.is_default,
        credentials: values.credentials || {},
      };

      if (editing) {
        await financeSettingsApi.updateOnlineGateway(editing.id, payload);
        message.success('درگاه با موفقیت ویرایش شد');
      } else {
        await financeSettingsApi.createOnlineGateway({ ...payload, driver: values.driver });
        message.success('درگاه با موفقیت اضافه شد');
      }

      closeModal();
      load();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error saving gateway:', error);
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره درگاه');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (record, checked) => {
    try {
      await financeSettingsApi.updateOnlineGateway(record.id, { is_active: checked });
      load();
    } catch (error) {
      message.error('خطا در تغییر وضعیت درگاه');
    }
  };

  const handleDelete = async (id) => {
    try {
      await financeSettingsApi.deleteOnlineGateway(id);
      message.success('درگاه حذف شد');
      load();
    } catch (error) {
      message.error('خطا در حذف درگاه');
    }
  };

  const handleSaveLinkSettings = async () => {
    try {
      const values = await linkForm.validateFields();
      setLinkSaving(true);
      await financeSettingsApi.updatePublicLinkSettings({
        public_link_expiry_days: values.mode === 'never' ? null : values.days,
      });
      message.success('تنظیمات لینک ذخیره شد');
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) {
        show422Error(error, linkForm);
      } else {
        message.error('خطا در ذخیره تنظیمات لینک');
      }
    } finally {
      setLinkSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    setDomainSaving(true);
    setDomainError(null);
    try {
      const res = await financeSettingsApi.updatePaymentDomain(domainInput.trim() || null);
      const data = res?.data || res;
      setDomain(data);
      setDomainInput(data?.payment_domain || '');
      message.success('دامنه ذخیره شد');
    } catch (error) {
      const detail = error instanceof ValidationError ? Object.values(error.details || {}).flat()[0] : null;
      setDomainError(detail || error?.message || 'خطا در ذخیره دامنه');
    } finally {
      setDomainSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setDomainChecking(true);
    setDomainError(null);
    try {
      const res = await financeSettingsApi.verifyPaymentDomain();
      const data = res?.data || res;
      setDomain(data);
      if (data?.checks?.verified) {
        message.success('دامنه تأیید شد');
      } else if (!data?.checks?.dns) {
        setDomainError('رکورد DNS دامنه هنوز به سرور ما اشاره نمی‌کند. اگر تازه آن را ثبت کرده‌اید، کمی بعد دوباره بررسی کنید.');
      } else {
        setDomainError('DNS درست است، ولی گواهی SSL این دامنه هنوز آماده نشده. لطفاً با پشتیبانی تماس بگیرید.');
      }
    } catch (error) {
      setDomainError(error?.message || 'خطا در بررسی دامنه');
    } finally {
      setDomainChecking(false);
    }
  };

  const hasLiveGateway = gateways.some((g) => g.is_active && !g.is_sandbox);
  const domainChanged = domainInput.trim() !== (domain?.payment_domain || '');

  const columns = [
    {
      title: 'عنوان',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className="flex items-center gap-2">
          <CreditCardOutlined className="text-blue-500" />
          <span className="font-medium">{text}</span>
          {record.is_default && (
            <Tooltip title="درگاه پیش‌فرض">
              <StarFilled className="text-amber-400" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'درگاه',
      dataIndex: 'driver_label',
      key: 'driver_label',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.is_sandbox && <Tag color="orange">آزمایشی</Tag>}
        </Space>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 140,
      align: 'center',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => handleToggleActive(record, checked)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} className="text-blue-500" />
          </Tooltip>
          <Popconfirm
            title="حذف درگاه"
            description="آیا از حذف این درگاه اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله، حذف کن"
            cancelText="خیر"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="حذف">
              <Button type="text" icon={<DeleteOutlined />} className="text-red-500" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <TableSkeleton rows={4} columns={4} />;
  }

  return (
    <div style={font} className="space-y-4">
      <Alert
        type="info"
        showIcon
        style={font}
        message="مشتری از طریق لینک عمومی فاکتور، کل مبلغ فاکتور را با درگاه‌های فعال شما پرداخت می‌کند و پرداخت به‌صورت خودکار ثبت می‌شود."
      />

      <Table
        columns={columns}
        dataSource={gateways}
        rowKey="id"
        pagination={false}
        scroll={{ x: 700 }}
        locale={{
          emptyText: (
            <Empty
              description={<span style={font}>هنوز درگاه آنلاینی وصل نشده</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      {hasLiveGateway && !domain?.verified ? (
        <Alert
          type="warning"
          showIcon
          style={font}
          message="تا وقتی دامنه‌ی اختصاصی شرکت تأیید نشود، درگاه‌های واقعی به مشتری نمایش داده نمی‌شوند."
        />
      ) : null}

      <Card
        size="small"
        title={<span style={font}><GlobalOutlined className="ml-2" />دامنه‌ی اختصاصی شرکت</span>}
        extra={domain?.payment_domain ? (
          <Tag color={domain.verified ? 'success' : 'warning'} style={font}>
            {domain.verified ? 'تأییدشده' : 'تأییدنشده'}
          </Tag>
        ) : null}
        className="border border-gray-100 dark:border-gray-700"
      >
        <p className="text-gray-500 text-sm mt-0">
          همان دامنه‌ای را وارد کنید که درگاه پرداخت با آن گرفته‌اید. پنل شرکت، لینک فاکتور و بازگشت از درگاه روی این دامنه انجام می‌شود.
        </p>
        <Space.Compact className="w-full max-w-lg">
          <Input
            dir="ltr"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="crm.example.ir"
          />
          <Button type="primary" onClick={handleSaveDomain} loading={domainSaving} disabled={!domainChanged} style={font}>
            ذخیره
          </Button>
          <Button onClick={handleVerifyDomain} loading={domainChecking} disabled={!domain?.payment_domain || domainChanged} style={font}>
            بررسی دامنه
          </Button>
        </Space.Compact>

        {domainError ? <Alert className="mt-3" type="error" showIcon message={domainError} style={font} /> : null}

        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="font-medium mb-1">۱. ثبت رکورد DNS برای این دامنه</div>
            {domain?.dns_target ? (
              <div>رکورد CNAME به <Typography.Text code copyable dir="ltr">{domain.dns_target}</Typography.Text></div>
            ) : null}
            {(domain?.dns_ips || []).length > 0 ? (
              <div>
                {domain?.dns_target ? 'یا رکورد A به ' : 'رکورد A به '}
                {domain.dns_ips.map((ip) => (
                  <Typography.Text key={ip} code copyable dir="ltr">{ip}</Typography.Text>
                ))}
              </div>
            ) : null}
            {!domain?.dns_target && !(domain?.dns_ips || []).length ? (
              <div className="text-gray-500">این اطلاعات هنوز تنظیم نشده؛ از پشتیبانی بگیرید.</div>
            ) : null}
          </div>
          <div>
            <div className="font-medium mb-1">۲. باز کردن IP سرور در پنل درگاه</div>
            {(domain?.outbound_ips || []).length > 0 ? (
              domain.outbound_ips.map((ip) => (
                <Typography.Text key={ip} code copyable dir="ltr">{ip}</Typography.Text>
              ))
            ) : (
              <div className="text-gray-500">این اطلاعات هنوز تنظیم نشده؛ از پشتیبانی بگیرید.</div>
            )}
          </div>
          <div className="font-medium">۳. زدن دکمه‌ی «بررسی دامنه»</div>
        </div>
      </Card>

      <Card
        size="small"
        title={<span style={font}><LinkOutlined className="ml-2" />اعتبار لینک عمومی فاکتور</span>}
        className="border border-gray-100 dark:border-gray-700"
      >
        <Form form={linkForm} layout="inline" style={font} className="gap-y-3">
          <Form.Item name="mode">
            <Radio.Group>
              <Radio value="days">منقضی شود بعد از</Radio>
              <Radio value="never">بدون انقضا</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.mode !== cur.mode}>
            {({ getFieldValue }) => (
              <Form.Item
                name="days"
                rules={getFieldValue('mode') === 'days' ? [{ required: true, message: 'تعداد روز را وارد کنید' }] : []}
              >
                <InputNumber
                  min={1}
                  max={3650}
                  addonAfter="روز"
                  disabled={getFieldValue('mode') === 'never'}
                  style={{ width: 140 }}
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSaveLinkSettings} loading={linkSaving} style={font}>
              ذخیره
            </Button>
          </Form.Item>
        </Form>
        <p className="text-gray-500 text-xs mt-2 mb-0">
          مدت اعتبار هنگام ساخت هر لینک روی آن ثبت می‌شود و تغییر این تنظیم روی لینک‌های قبلاً ارسال‌شده اثری ندارد.
        </p>
      </Card>

      <Modal
        open={modalVisible}
        onCancel={closeModal}
        title={
          <div className="flex items-center gap-2" style={font}>
            <CreditCardOutlined className="text-blue-500" />
            <span>{editing ? 'ویرایش درگاه آنلاین' : 'اتصال درگاه آنلاین'}</span>
          </div>
        }
        footer={null}
        centered
        width={520}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={font}>
          <Form.Item label="درگاه" name="driver" rules={[{ required: true, message: 'درگاه را انتخاب کنید' }]}>
            <Select
              disabled={!!editing}
              options={drivers.map((d) => ({ value: d.driver, label: d.label }))}
            />
          </Form.Item>

          <Form.Item label="عنوان (برای مشتری نمایش داده می‌شود)" name="title" rules={[{ required: true, message: 'عنوان الزامی است' }]}>
            <Input placeholder="مثلاً: پرداخت آنلاین" />
          </Form.Item>

          {(selectedDriver?.fields || []).map((field) => {
            const stored = editing?.credentials?.[field.name];
            const keepExisting = !!editing && field.secret && !!stored;
            const InputComponent = field.secret || field.type === 'password' ? Input.Password : Input;

            return (
              <Form.Item
                key={field.name}
                label={field.label}
                name={['credentials', field.name]}
                extra={keepExisting ? `مقدار فعلی: ${stored} — برای تغییر نکردن خالی بگذارید` : field.help}
                rules={field.required && !keepExisting ? [{ required: true, message: `${field.label} الزامی است` }] : []}
              >
                <InputComponent dir="ltr" autoComplete="off" />
              </Form.Item>
            );
          })}

          <div className="flex flex-wrap gap-6">
            <Form.Item label="فعال" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="پیش‌فرض" name="is_default" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="حالت آزمایشی (sandbox)" name="is_sandbox" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button onClick={closeModal} style={font}>انصراف</Button>
            <Button type="primary" htmlType="submit" loading={saving} className="bg-green-600 hover:bg-green-700 border-0" style={font}>
              {editing ? 'ذخیره تغییرات' : 'اتصال درگاه'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
});

export default OnlineGatewaysTab;
